import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Body1,
  Caption1,
  Dropdown,
  Field,
  makeStyles,
  Option,
  Subtitle1,
  Tab,
  TabList,
  ToggleButton,
  tokens,
} from '@fluentui/react-components';
import { DocumentBulletList20Regular, People20Regular } from '@fluentui/react-icons';

import { useDocumentTitle } from '@shared/hooks';
import { NoPage } from '@shared/pages';

import TrainingRecordsTable from '../components/records/TrainingRecordsTable';
import useTrainingMatrix from '../hooks/useTrainingMatrix';
import { TrainingPageScaffold } from '../layouts/TrainingPageScaffold';
import { TTrainingRecord } from '../types';
import { getRecordsForDocument, getRecordsForEmployee, isTrainedStatus } from '../utils/trainingMatrix';

type TView = 'employee' | 'document';
type TDocumentFilter = 'all' | 'trained' | 'not-trained';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  controls: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
  },
  picker: {
    minWidth: '320px',
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '130px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statValue: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

const Stat = ({ label, value }: { label: string; value: string | number }) => {
  const classes = useStyles();
  return (
    <div className={classes.stat}>
      <span className={classes.statValue}>{value}</span>
      <Caption1 className={classes.muted}>{label}</Caption1>
    </div>
  );
};

const countBy = (records: TTrainingRecord[], predicate: (record: TTrainingRecord) => boolean) =>
  records.filter(predicate).length;

// QM training records prototype: query what an employee was trained on, or who is
// (not) trained on a document. Selection lives in the URL so views can be linked.
const TrainingRecordsPage = () => {
  useDocumentTitle('Training records');
  const classes = useStyles();
  const matrix = useTrainingMatrix();
  const [searchParams, setSearchParams] = useSearchParams();

  const view: TView = searchParams.get('view') === 'document' ? 'document' : 'employee';
  const filter = (searchParams.get('filter') as TDocumentFilter | null) ?? 'all';
  const employeeId = searchParams.get('employee') ?? matrix?.employees[0]?.id ?? '';
  const documentId = searchParams.get('document') ?? matrix?.documents[0]?.id ?? '';

  const updateParams = (next: Record<string, string>) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(next).forEach(([key, value]) => params.set(key, value));
      return params;
    });

  const employeeRecords = useMemo(
    () => (matrix ? getRecordsForEmployee(matrix, employeeId) : []),
    [matrix, employeeId]
  );
  const documentRecords = useMemo(
    () => (matrix ? getRecordsForDocument(matrix, documentId) : []),
    [matrix, documentId]
  );

  // Gated by `organization.mock.training` - without it the page does not exist.
  if (!matrix) return <NoPage />;

  const employee = matrix.employees.find(({ id }) => id === employeeId);
  const document = matrix.documents.find(({ id }) => id === documentId);

  const filteredDocumentRecords = documentRecords.filter((record) =>
    filter === 'trained'
      ? isTrainedStatus(record.status)
      : filter === 'not-trained'
        ? !isTrainedStatus(record.status)
        : true
  );

  const selectEmployee = (id: string) => updateParams({ view: 'employee', employee: id });
  const selectDocument = (id: string) => updateParams({ view: 'document', document: id, filter: 'all' });

  return (
    <TrainingPageScaffold>
      <div className={classes.header}>
        <Subtitle1>Training records</Subtitle1>
        <Body1 className={classes.muted}>
          Look up what an employee has been trained on, or who is trained on a document.
        </Body1>
      </div>

      <TabList selectedValue={view} onTabSelect={(_, data) => updateParams({ view: data.value as TView })}>
        <Tab value="employee" icon={<People20Regular />}>
          By employee
        </Tab>
        <Tab value="document" icon={<DocumentBulletList20Regular />}>
          By document
        </Tab>
      </TabList>

      {view === 'employee' ? (
        <>
          <div className={classes.controls}>
            <Field label="Employee">
              <Dropdown
                className={classes.picker}
                value={employee ? `${employee.name} (${employee.function})` : ''}
                selectedOptions={[employeeId]}
                onOptionSelect={(_, data) => data.optionValue && selectEmployee(data.optionValue)}
              >
                {matrix.employees.map((option) => (
                  <Option key={option.id} value={option.id} text={`${option.name} (${option.function})`}>
                    {option.name} ({option.function})
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>

          <div className={classes.summary}>
            <Stat label="Trainings on record" value={employeeRecords.length} />
            <Stat label="Current" value={countBy(employeeRecords, (record) => isTrainedStatus(record.status))} />
            <Stat
              label="Open / overdue"
              value={countBy(employeeRecords, ({ status }) => status === 'open' || status === 'overdue')}
            />
            <Stat
              label="Gaps (not trained / outdated)"
              value={countBy(employeeRecords, ({ status }) => status === 'not-trained' || status === 'outdated')}
            />
          </div>

          <TrainingRecordsTable
            records={employeeRecords}
            subject="employee"
            onSelectEmployee={selectEmployee}
            onSelectDocument={selectDocument}
            emptyMessage="No training requirements or records for this employee."
          />
        </>
      ) : (
        <>
          <div className={classes.controls}>
            <Field label="Document">
              <Dropdown
                className={classes.picker}
                value={document ? `${document.name} (${document.revision})` : ''}
                selectedOptions={[documentId]}
                onOptionSelect={(_, data) => data.optionValue && selectDocument(data.optionValue)}
              >
                {matrix.documents.map((option) => (
                  <Option key={option.id} value={option.id} text={`${option.name} (${option.revision})`}>
                    {option.name} ({option.revision})
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {document && (
              <Caption1 className={classes.muted}>
                Required for: {document.targetFunctions.join(', ')} · Method: {document.trainingMethod}
              </Caption1>
            )}
          </div>

          <div className={classes.summary}>
            <Stat
              label="Trained on current revision"
              value={`${countBy(documentRecords, (record) => isTrainedStatus(record.status))} / ${documentRecords.length}`}
            />
            <Stat label="Open" value={countBy(documentRecords, ({ status }) => status === 'open')} />
            <Stat label="Overdue" value={countBy(documentRecords, ({ status }) => status === 'overdue')} />
            <Stat label="Outdated revision" value={countBy(documentRecords, ({ status }) => status === 'outdated')} />
            <Stat label="Not trained" value={countBy(documentRecords, ({ status }) => status === 'not-trained')} />
          </div>

          <div className={classes.filters}>
            {(
              [
                ['all', 'All'],
                ['trained', 'Trained'],
                ['not-trained', 'Not trained'],
              ] as const
            ).map(([value, label]) => (
              <ToggleButton
                key={value}
                size="small"
                shape="circular"
                checked={filter === value}
                onClick={() => updateParams({ filter: value })}
              >
                {label}
              </ToggleButton>
            ))}
          </div>

          <TrainingRecordsTable
            records={filteredDocumentRecords}
            subject="document"
            onSelectEmployee={selectEmployee}
            onSelectDocument={selectDocument}
            emptyMessage="No employees match this filter."
          />
        </>
      )}
    </TrainingPageScaffold>
  );
};

export default TrainingRecordsPage;
